/**
 * What a tracker's rows are, as the product offers it. Chosen once at creation
 * and immutable afterward — switching source later would strand the board's
 * columns and values.
 *
 * The create dialog, the analytics dimension and the starting layout all read
 * this table, so a new item type is an entry here rather than an edit in three
 * places.
 */
import type { TrackerItemSource } from '@inference.engine/workspace-api-client/web';
import type { ParseKeys } from 'i18next';
import {
  FileStackIcon,
  FileTextIcon,
  FolderKanbanIcon,
  TypeIcon,
  type LucideIcon,
} from 'lucide-react';

/**
 * The `board_type` every `tracker_*` event carries. A `text` board reports as
 * `free_form`; the rest report under their own name.
 */
export type TrackerBoardType = 'free_form' | 'briefs' | 'projects' | 'assets';

/**
 * Which half of the chooser a type sits under. The heading carries the meaning
 * the row descriptions would otherwise each have to repeat.
 */
export type TrackerItemSourceGroup = 'blank' | 'records';

export interface TrackerItemSourceOption {
  id: TrackerItemSource;
  group: TrackerItemSourceGroup;
  boardType: TrackerBoardType;
  labelKey: ParseKeys<'trackers'>;
  descriptionKey: ParseKeys<'trackers'>;
  Icon: LucideIcon;
  /** Grouping a new board of this type starts on, when no starter template sets one. */
  defaultGroupBy?: string;
  /**
   * Built-in template supplying the grouping and columns a new board of this
   * type opens with, so it is usable without configuring anything.
   */
  starterTemplateKey?: string;
}

export const TRACKER_ITEM_SOURCE_GROUPS: readonly {
  id: TrackerItemSourceGroup;
  labelKey: ParseKeys<'trackers'>;
}[] = [
  { id: 'blank', labelKey: 'trackers.startEmpty' },
  { id: 'records', labelKey: 'trackers.fromRecordsYouAlreadyHave' },
];

/* eslint-disable i18next/no-literal-string -- wire values and grouping keys persisted on the tracker, not copy */
export const TRACKER_ITEM_SOURCES: readonly TrackerItemSourceOption[] = [
  {
    id: 'text',
    group: 'blank',
    boardType: 'free_form',
    labelKey: 'trackers.freeForm',
    descriptionKey: 'trackers.itemsYouTypeDirectlyOn',
    Icon: TypeIcon,
  },
  {
    id: 'briefs',
    group: 'records',
    boardType: 'briefs',
    labelKey: 'trackers.briefs',
    descriptionKey: 'trackers.groupedByProject',
    Icon: FileTextIcon,
    defaultGroupBy: 'project',
  },
  {
    id: 'projects',
    group: 'records',
    boardType: 'projects',
    labelKey: 'trackers.projects',
    descriptionKey: 'trackers.withStatusOwnerAndDeadline',
    Icon: FolderKanbanIcon,
    starterTemplateKey: 'projects-board',
  },
  {
    id: 'assets',
    group: 'records',
    boardType: 'assets',
    labelKey: 'trackers.assets',
    descriptionKey: 'trackers.withApprovalState',
    Icon: FileStackIcon,
    starterTemplateKey: 'assets-board',
  },
];
/* eslint-enable i18next/no-literal-string */

const BOARD_TYPE_BY_SOURCE = new Map(TRACKER_ITEM_SOURCES.map((o) => [o.id, o.boardType]));

/** Map a tracker's `itemSource` to the analytics board-type dimension. */
export function boardTypeOf(itemSource: string | null | undefined): TrackerBoardType {
  return BOARD_TYPE_BY_SOURCE.get(itemSource as TrackerItemSource) ?? 'free_form';
}
